import styled, { keyframes } from "styled-components";

// #region SCOPED CSS
const amberSweep = keyframes`
    from { transform: translateX(-100%); }
    to { transform: translateX(300%); }
`;

const LoadingCard = styled.div`
    box-sizing: border-box;
    width: ${({ $compact }) => $compact ? "100%" : "360px"};
    max-width: 100%;
    min-width: 0;
    flex-shrink: 0;
    padding: ${({ $compact }) => $compact ? "12px" : "16px 24px 24px"};
    background: #17191b;
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 12px;
    box-shadow: 0 16px 40px rgba(0,0,0,0.35);
    font-family: Roboto, sans-serif;
    text-align: center;
    overflow-wrap: anywhere;

    .premium-loading-title {
        margin: 0 0 ${({ $compact }) => $compact ? "12px" : "16px"};
        color: #efbf73;
        font-size: 12px;
        font-weight: 500;
        line-height: 12px;
        letter-spacing: 0.0125em;
        text-transform: uppercase;
    }
    .premium-loading-divider { height: 1px; background: rgba(255,255,255,0.10); }
    .premium-loading-header {
        margin: ${({ $compact }) => $compact ? "12px" : "28px"} 0 0;
        color: #fff;
        font-size: 18px;
        font-weight: 600;
        line-height: 21.04px;
    }
    .premium-loading-slider {
        display: flex;
        justify-content: center;
        align-items: center;
        height: ${({ $compact }) => $compact ? "36px" : "64px"};
    }
    .premium-loading-track {
        width: 220px;
        max-width: 100%;
        height: 6px;
        overflow: hidden;
        border: 1px solid rgba(222, 172, 94, 0.24);
        border-radius: 999px;
        background: rgba(23, 25, 26, 0.88);
        box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.5);
        box-sizing: border-box;
    }
    .premium-loading-sweep {
        display: block;
        width: 50%;
        height: 100%;
        border-radius: inherit;
        background: linear-gradient(90deg, transparent, #c58c36 30%, #efbf73 65%, transparent);
        animation: ${amberSweep} 1.8s ease-in-out infinite;
    }
    .premium-loading-footer {
        margin: ${({ $compact }) => $compact ? "12px 0 0" : "0"};
        color: #d1d1d1;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
    }
    @media (prefers-reduced-motion: reduce) {
        .premium-loading-sweep { animation: none; transform: translateX(50%); }
    }
`;
// #endregion

/**
 * Presentation-only indeterminate loading card. Mount only while work is pending.
 * @param {Object} props
 * @param {string} [props.title="LOADING..."] Always-visible amber status title.
 * @param {string} [props.header] Optional main message; blank text renders no section.
 * @param {string} [props.footer] Optional explanation; blank text renders no section.
 * @param {boolean} [props.showHeader=true] Independently enable the main message.
 * @param {boolean} [props.showFooter=true] Independently enable the explanation.
 * @param {boolean} [props.compact=false] Fill the containing panel with compact spacing.
 */
export default function PremiumLoadingCard({ title = "LOADING...", header, footer,
    showHeader = true, showFooter = true, compact = false }) {
    const hasHeader = showHeader && typeof header === "string" && header.trim().length > 0;
    const hasFooter = showFooter && typeof footer === "string" && footer.trim().length > 0;

    return <LoadingCard $compact={compact} role="status" aria-live="polite" aria-atomic="true" aria-busy="true">
        <p className="premium-loading-title">{title}</p>
        <div className="premium-loading-divider" aria-hidden="true" />
        {hasHeader && <p className="premium-loading-header">{header}</p>}
        <div className="premium-loading-slider" aria-hidden="true">
            <span className="premium-loading-track"><span className="premium-loading-sweep" /></span>
        </div>
        {hasFooter && <p className="premium-loading-footer">{footer}</p>}
    </LoadingCard>;
}
